import React, { forwardRef, useId } from 'react';
import { cn } from '@/lib/cn';
import { IconAlertCircleFilled } from '@repo/svgs';

export type InputVariant = 'default' | 'filled';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * Optional label shown above the field. When provided we wire it to the input via `htmlFor`.
   */
  label?: string;
  /**
   * Helper text rendered under the field. Accepts either a string or any React node.
   */
  helperText?: React.ReactNode;
  /**
   * When `true` we render the alert treatment (red border, helper icon, etc.).
   */
  alert?: boolean;
  /**
   * Override helper icon. Defaults to the alert icon when `alert` is true.
   */
  helperIcon?: React.ReactNode;
  /**
   * Visual variant from the design system. `filled` keeps the content colourised even without a value.
   */
  variant?: InputVariant;
  /**
   * Class applied to the wrapper that contains label, field and helper text.
   */
  wrapperClassName?: string;
  /**
   * Optional icon displayed on the left side of the input field.
   */
  leftIcon?: React.ReactNode;
  /**
   * Optional icon displayed on the right side of the input field.
   */
  rightIcon?: React.ReactNode;
  /**
   * Optional overlay rendered inside the field wrapper (positioned within the pill).
   * Useful for custom placeholders/overlays (e.g. DateInput on mobile).
   *
   * NOTE: Consumers should generally add `pointer-events-none` to overlays unless the overlay
   * intentionally intercepts input interactions.
   */
  fieldOverlay?: React.ReactNode;
  /**
   * Optional click handler for the FIELD wrapper (the pill), not the <input>.
   * Useful when the whole field (including icons) should be clickable, e.g. DateInput.
   */
  fieldOnClick?: React.MouseEventHandler<HTMLDivElement>;
  /**
   * Optional pointer down handler for the FIELD wrapper (the pill), not the <input>.
   */
  fieldOnPointerDown?: React.PointerEventHandler<HTMLDivElement>;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    helperText,
    helperIcon,
    alert = false,
    variant = 'default',
    wrapperClassName,
    className,
    disabled,
    id,
    leftIcon,
    rightIcon,
    fieldOverlay,
    fieldOnClick,
    fieldOnPointerDown,
    ...inputProps
  },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const helperId = helperText ? `${inputId}-helper` : undefined;
  const showAlert = alert && !disabled;
  const isFilled = variant === 'filled';

  const fieldClasses = cn(
    'relative flex h-11 w-full items-center gap-2 rounded-full px-4 border ring-inset',
    disabled
      ? // Disabled: muted background + border, no hover/focus treatment
        'cursor-not-allowed bg-secondary border-secondary'
      : cn(
          'bg-primary hover:bg-primary-hover focus-within:ring-2',
          showAlert
            ? 'border-alert focus-within:ring-alert'
            : 'border-secondary focus-within:ring-accent',
        ),
  );

  const inputTextClasses = cn(
    'w-full bg-transparent text-body-2 outline-none',
    // Put consumer className BEFORE our state styles so disabled styles always win.
    className,
    disabled ? '!text-tertiary !placeholder:text-tertiary' : 'text-primary',
    !disabled ? '!placeholder:text-tertiary' : null,
  );

  const tintIconForDisabled = (icon: React.ReactNode) => {
    if (!disabled) return icon;
    if (!React.isValidElement(icon)) return icon;
    const existing = (icon.props as any)?.className;
    return React.cloneElement(icon as any, {
      className: cn(existing, '!text-tertiary !icon-tertiary'),
    });
  };

  return (
    <div className={cn('flex w-full flex-col gap-1', wrapperClassName)}>
      {label ? (
        <label htmlFor={inputId} className={cn('text-body-2-high text-secondary')}>
          {label}
        </label>
      ) : null}

      <div
        className={fieldClasses}
        onClick={disabled ? undefined : fieldOnClick}
        onPointerDown={disabled ? undefined : fieldOnPointerDown}
      >
        {fieldOverlay ? fieldOverlay : null}
        {leftIcon && <div className="flex-shrink-0">{tintIconForDisabled(leftIcon)}</div>}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={showAlert || undefined}
          aria-describedby={helperId}
          disabled={disabled}
          className={inputTextClasses}
          {...inputProps}
        />
        {rightIcon && (
          <div className="flex-shrink-0 pointer-events-none">{tintIconForDisabled(rightIcon)}</div>
        )}
      </div>

      {helperText ? (
        <div className={cn('flex items-center gap-1')} id={helperId}>
          {showAlert ? (
            // Safari: some cases don't reliably apply our `icon-*` utility color to SVG currentColor.
            // Use the standard text color utility to ensure the alert icon always paints.
            <IconAlertCircleFilled className="h-4 w-4 text-alert" />
          ) : null}
          <span className={cn('flex-1 text-caption ', showAlert ? 'text-alert' : 'text-secondary')}>
            {helperText}
          </span>
        </div>
      ) : null}
    </div>
  );
});

export default Input;
